// 010A — Laboratorio Clínico Solicitud (Anexo 1 MSP, SNS-MSP/HCU-form.010A/2021). Fase 7, Tanda 14: hoja 1 (servicio y
// prioridad con 2 diagnósticos CIE, listado de exámenes en 16 grupos con las casillas impresas, biología molecular, toma
// de muestra) y hoja 2 (VIH/ITS y tuberculosis, plegadas). Catálogo y decisiones en `catalogo010ADatos.ts`. El bloque A lo
// resuelve `EncabezadoMspCampos`; el profesional lo arma el servidor en el PDF firmado.
import { useMedplum } from '@medplum/react-hooks';
import type { ServiceRequest } from '@medplum/fhirtypes';
import type { JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import type { FormularioDiscretoProps } from './index';
import { AreaDictado } from './AreaDictado';
import { CampoDictado } from './CampoDictado';
import { ErrorGuardado } from './ErrorGuardado';
import { useGuardado } from './useGuardado';
import { CATEGORIA_LABORATORIO } from './categoriasDiagnosticas';
import { Bloque, CamposSimples } from './camposSimples';
import type { CampoSimple, Valores } from './camposSimplesDatos';
import { codeDeDiagnostico, SelectorDiagnosticoCie10 } from './SelectorDiagnosticoCie10';
import { EncabezadoMspCampos, valoresInicialesEncabezadoMsp } from '../EncabezadoMspCampos';
import { useDatosEncabezadoMsp } from '../useDatosEncabezadoMsp';
import {
  CAMPOS_MICROBIOLOGIA_010A,
  CAMPOS_SERVICIO_010A,
  CAMPOS_TB_AFECTADO_010A,
  CAMPOS_TB_ANTECEDENTES_010A,
  CAMPOS_TB_MUESTRA_010A,
  CAMPOS_TB_SOLICITUD_010A,
  CAMPOS_TEXTOS_LIBRES_010A,
  CAMPOS_TOMA_MUESTRA_010A,
  CAMPOS_VIH_ITS_010A,
  claveLibre,
  GRUPOS_EXAMENES_010A,
  LIBRES_010A,
} from './catalogo010ADatos';
import { claveExamen, conExamenMarcado, examenMarcado, conTextosMicrobiologia, estadoInicial010A, paresDe010A, prioridadFhir010A, textoSolicitud010A, type Estado010A } from './bloques010ADatos';

type ClaveValores = 'servicio' | 'microbiologia' | 'textos' | 'tomaMuestra' | 'vihIts' | 'tbAfectado' | 'tbAntecedentes' | 'tbMuestra' | 'tbSolicitud';

export function Formulario010A({ subject, encounter, onGuardado }: FormularioDiscretoProps): JSX.Element {
  const { t } = useTranslation();
  const medplum = useMedplum();
  const [estado, setEstado] = useState<Estado010A>(() => estadoInicial010A());
  const [campos, setCampos] = useState(() => valoresInicialesEncabezadoMsp());
  const { guardando, error, guardar } = useGuardado();
  const datosEncabezadoMsp = useDatosEncabezadoMsp(subject, campos, '010A', t('formularios.f010a.titulo'), { servicio: estado.servicio.servicio });

  const simples = (clave: ClaveValores, prefijo: string, lista: readonly CampoSimple[], columnas: 2 | 3 | 4): JSX.Element => {
    const valores: Valores = estado[clave];
    return <CamposSimples prefijoId={`f010a-${prefijo}`} campos={lista} valores={valores} columnas={columnas} onChange={(v) => setEstado({ ...estado, [clave]: { ...valores, ...v } })} />;
  };

  const handleSubmit = (): Promise<void> =>
    guardar(async () => {
      const reasonCode = estado.diagnosticos.filter((d) => d.texto.trim()).map((d) => codeDeDiagnostico(d));
      const toma = (estado.tomaMuestra.fecha ?? '').trim();
      await medplum.createResource<ServiceRequest>({
        resourceType: 'ServiceRequest',
        status: 'active',
        intent: 'order',
        category: [CATEGORIA_LABORATORIO],
        priority: prioridadFhir010A(estado.servicio.prioridad),
        code: { text: textoSolicitud010A(estado) },
        subject,
        encounter,
        authoredOn: new Date().toISOString(),
        occurrenceDateTime: toma || undefined,
        reasonCode: reasonCode.length ? reasonCode : undefined,
      });
      onGuardado(paresDe010A(estado), datosEncabezadoMsp);
    });

  return (
    <div className="flex flex-col gap-6">
      <h3 className="text-base font-medium text-hce-ink">{t('formularios.f010a.titulo')}</h3>

      <EncabezadoMspCampos subject={subject} value={campos} onChange={setCampos} />

      <Bloque titulo={t('formularios.f010a.servicioTitulo')}>
        {estado.diagnosticos.map((d, i) => (
          <SelectorDiagnosticoCie10 key={i} label={`${t('formularios.f010a.diagnostico')} ${i + 1}`} value={d} onChange={(v) => setEstado({ ...estado, diagnosticos: estado.diagnosticos.map((x, j) => (j === i ? v : x)) })} />
        ))}
        {simples('servicio', 'srv', CAMPOS_SERVICIO_010A, 3)}
      </Bloque>

      <Bloque titulo={t('formularios.f010a.examenesTitulo')}>
        <p className="text-xs text-hce-muted">{t('formularios.f010a.examenesNota')}</p>
        {GRUPOS_EXAMENES_010A.map((g) => (
          <fieldset key={g.clave} className="flex flex-col gap-2 rounded-md border border-border p-3">
            <legend className="px-1 text-sm font-medium text-hce-ink">{g.etiqueta}</legend>
            <div className="grid grid-cols-1 gap-x-4 gap-y-1.5 sm:grid-cols-2 lg:grid-cols-3">
              {g.items.map((item, i) => {
                const clave = claveExamen(g.clave, i);
                return (
                  <label key={clave} className="flex items-center gap-2 text-sm text-hce-ink">
                    <Checkbox checked={examenMarcado(estado, g.clave, i)} onCheckedChange={(v) => setEstado(conExamenMarcado(estado, g.clave, i, v === true))} />
                    {item}
                  </label>
                );
              })}
            </div>
            {g.clave === 'microbiologia' && (
              <CamposSimples prefijoId="f010a-mic" campos={CAMPOS_MICROBIOLOGIA_010A} valores={estado.microbiologia} columnas={3} onChange={(v) => setEstado(conTextosMicrobiologia(estado, v))} />
            )}
            {Array.from({ length: LIBRES_010A[g.clave] ?? 0 }, (_, n) => {
              const clave = claveLibre(g.clave, n);
              return (
                <CampoDictado
                  key={clave}
                  id={`f010a-libre-${clave}`}
                  etiqueta={`${g.etiqueta} — ${t('formularios.f010a.filaLibre')} ${n + 1}`}
                  placeholder={t('formularios.f010a.filaLibre')}
                  valor={estado.libres[clave] ?? ''}
                  onChange={(v) => setEstado({ ...estado, libres: { ...estado.libres, [clave]: v } })}
                />
              );
            })}
          </fieldset>
        ))}
        <AreaDictado
          id="f010a-biomol"
          etiqueta={CAMPOS_TEXTOS_LIBRES_010A[0]!.etiqueta}
          valor={estado.textos.biologiaMolecular ?? ''}
          filas={4}
          onChange={(v) => setEstado({ ...estado, textos: { ...estado.textos, biologiaMolecular: v } })}
        />
      </Bloque>

      <Bloque titulo={t('formularios.f010a.tomaMuestraTitulo')}>{simples('tomaMuestra', 'toma', CAMPOS_TOMA_MUESTRA_010A, 3)}</Bloque>

      <details className="rounded-md border border-border p-3">
        <summary className="cursor-pointer text-sm font-medium text-hce-ink">{t('formularios.f010a.hoja2Titulo')}</summary>
        <div className="mt-3 flex flex-col gap-4">
          <Bloque titulo={t('formularios.f010a.vihTitulo')}>{simples('vihIts', 'vih', CAMPOS_VIH_ITS_010A, 4)}</Bloque>
          <Bloque titulo={t('formularios.f010a.tbTitulo')}>
            <p className="text-sm font-medium text-hce-ink">{t('formularios.f010a.tbAfectado')}</p>
            {simples('tbAfectado', 'tba', CAMPOS_TB_AFECTADO_010A, 3)}
            <p className="text-sm font-medium text-hce-ink">{t('formularios.f010a.tbAntecedentes')}</p>
            {simples('tbAntecedentes', 'tbn', CAMPOS_TB_ANTECEDENTES_010A, 3)}
            <p className="text-sm font-medium text-hce-ink">{t('formularios.f010a.tbMuestra')}</p>
            {simples('tbMuestra', 'tbm', CAMPOS_TB_MUESTRA_010A, 3)}
            <p className="text-sm font-medium text-hce-ink">{t('formularios.f010a.tbSolicitud')}</p>
            {simples('tbSolicitud', 'tbs', CAMPOS_TB_SOLICITUD_010A, 3)}
          </Bloque>
        </div>
      </details>

      <Bloque titulo={t('formularios.f010a.profesionalTitulo')}>
        <p className="text-sm text-hce-muted">{t('formularios.profesionalNotaCedula')}</p>
      </Bloque>

      <ErrorGuardado error={error} />

      <Button type="button" className="w-fit" onClick={() => void handleSubmit()} disabled={guardando}>
        {t('formulario.guardar')}
      </Button>
    </div>
  );
}
